import { DisplayPreferencesService } from '../preferences/display-preferences.service';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../../../environments/environment';

export type LedgerAccountType = 'ASSET' | 'LIABILITY' | 'EQUITY' | 'INCOME' | 'EXPENSE';
export type AccountNormalBalance = 'DEBIT' | 'CREDIT';
export type ChartAccountInput = { accountCode: string; accountName: string; accountType: LedgerAccountType;
  normalBalance: AccountNormalBalance; description?: string; cashFlowCategory?: string };
export type BankAccountType = 'TRUST_OPERATING' | 'SECURITY_DEPOSIT_TRUST' | 'STRIPE_CLEARING' | 'PM_OPERATING' | string;
export type AccountingPeriodStatus = 'OPEN' | 'CLOSED' | 'REOPENED';
export type ReconciliationStatus = 'DRAFT' | 'BALANCED' | 'OUT_OF_BALANCE' | 'CLOSED';
export type BankStatementLineMatchStatus = 'UNMATCHED' | 'MATCHED' | 'IGNORED';
export type AccountingDirection = 'CREDIT' | 'DEBIT';
export type AccountingCapabilities = {
  organizationId: string;
  userId: string;
  canRead: boolean;
  canWrite: boolean;
  canConfigure: boolean;
  canClosePeriod: boolean;
  organizationOwner?: boolean | null;
  capabilities?: readonly string[] | null;
  enforced?: boolean | null;
};
export type AccountingHealth = { organizationId: string; activeBusinesses: number; activeBanks: number;
  unownedBanks: number; unownedTransactions: number; unownedPostingLines: number; missingJournals: number; pendingTransactions: number;
  businessesWithoutBookPolicy?: number; unreviewedBookPolicies?: number; settlementMappings?: number;
  settlementReviewItems?: number; banksWithoutClosedReconciliation?: number };
export type AccountantScope = { legalEntityId: string; from: string; to: string; currency: string; basis: string; propertyId?: string };
export type AccountantPreview = { organizationId: string; scope: AccountantScope; businessName: string; generatedAt: string;
  status: string; ledgerLines: number; warnings: string[]; files: string[] };
export type PostingRuleContextMode = 'ANY' | 'REQUIRED' | 'ABSENT';

export type ChartOfAccountDto = {
  id: string;
  accountCode: string;
  accountName: string;
  accountType: LedgerAccountType;
  normalBalance: AccountNormalBalance;
  systemAccount: boolean;
  active: boolean;
  description?: string | null;
  cashFlowCategory?: string | null;
};

export type ChartAccountEditView = {
  account: ChartOfAccountDto;
  version: number;
  structureEditable: boolean;
  canDeactivate: boolean;
  lockReasons: string[];
};

export type ChartAccountUpdateInput = {
  version: number;
  accountName: string;
  accountType: LedgerAccountType;
  normalBalance: AccountNormalBalance;
  description: string | null;
  cashFlowCategory: string | null;
  active: boolean;
};

export type AccountingBankAccountDto = {
  id: string;
  displayName: string;
  accountCode: string;
  bankAccountType: BankAccountType;
  currency: string;
  provider?: string | null;
  maskedAccountNumber?: string | null;
  trustAccount: boolean;
  defaultForRentReceipts: boolean;
  defaultForSecurityDeposits: boolean;
  defaultForOwnerPayouts: boolean;
  openingBalance?: number | null;
  openingBalanceDate?: string | null;
  active: boolean;
  legalEntityId?: string | null;
};

export type LegalEntityType = 'INDIVIDUAL' | 'SOLE_PROPRIETORSHIP' | 'SINGLE_MEMBER_LLC' | 'MULTI_MEMBER_LLC' | 'PARTNERSHIP'
  | 'C_CORPORATION' | 'S_CORPORATION' | 'TRUST_OR_ESTATE' | 'OTHER';
/**
 * Batch 2.8 (accountant-reviewed): INDIVIDUAL and DISREGARDED_ENTITY (follows its tax owner's return) replace the combined
 * INDIVIDUAL_OR_DISREGARDED; ESTATE, NON_GRANTOR_TRUST and GRANTOR_TRUST replace TRUST_OR_ESTATE. The legacy values can still
 * be read (they mark a business whose classification must be re-confirmed) but can no longer be chosen.
 */
export type FederalTaxClassification = 'INDIVIDUAL' | 'DISREGARDED_ENTITY' | 'PARTNERSHIP' | 'S_CORPORATION' | 'C_CORPORATION' | 'ESTATE'
  | 'NON_GRANTOR_TRUST' | 'GRANTOR_TRUST' | 'OTHER';
export type LegacyFederalTaxClassification = 'INDIVIDUAL_OR_DISREGARDED' | 'TRUST_OR_ESTATE';
export const LEGACY_TAX_CLASSIFICATIONS: readonly LegacyFederalTaxClassification[] = ['INDIVIDUAL_OR_DISREGARDED', 'TRUST_OR_ESTATE'];
/** Who owns a disregarded entity for federal tax; its figures follow this owner's return. */
export type TaxOwnerType = 'INDIVIDUAL' | 'CORPORATION' | 'PARTNERSHIP' | 'OTHER';
/** An entity classification election recorded separately: Form 8832, Form 2553 or another election. */
export type TaxClassificationElectionType = 'FORM_8832' | 'FORM_2553' | 'OTHER';
/** The legal form and the federal tax classification are recorded separately (batch 2.7); reports name a return only once confirmed. */
export type AccountingLegalEntity = {
  id: string; code: string; name: string; active: boolean; version?: number | null;
  legalEntityType?: LegalEntityType | null; federalTaxClassification?: FederalTaxClassification | LegacyFederalTaxClassification | null;
  taxClassificationEffectiveFrom?: string | null; taxClassificationDocumentation?: string | null;
  taxClassificationConfirmedBy?: string | null; taxClassificationConfirmedAt?: string | null; taxClassificationConfirmed?: boolean;
  /** Batch 2.8: a legacy classification (or one flagged by the migration) that must be confirmed again. */
  taxClassificationReconfirmationRequired?: boolean; taxClassificationNeedsReconfirmation?: boolean | null;
  taxOwnerType?: TaxOwnerType | null; taxOwnerName?: string | null; taxOwnerReference?: string | null;
  taxElectionType?: TaxClassificationElectionType | null; taxElectionEffectiveDate?: string | null; taxElectionReference?: string | null;
  /** Batch 2.9: when the election was filed, and the timing/eligibility warnings from the last save (warnings, not validation). */
  taxElectionFiledDate?: string | null; taxClassificationWarnings?: string[] | null;
  /**
   * Batch 2.12: the filing state whose statewide holidays also move deadlines (two letters; null = federal and DC holidays only), the
   * formation date and the "existence covered" confirmation (one classification history covering the whole existence).
   */
  filingState?: string | null; formationDate?: string | null; existenceCovered?: boolean | null;
  existenceCoveredConfirmedBy?: string | null; existenceCoveredConfirmedAt?: string | null;
};
/** Batch 2.12: a business's tax profile as saved. */
export type LegalEntityTaxProfile = { filingState: string | null; formationDate: string | null; existenceCovered: boolean;
  existenceCoveredConfirmedBy?: string | null; existenceCoveredConfirmedAt?: string | null; version: number | null };
export type LegalEntityTaxProfileInput = { filingState: string | null; formationDate: string | null; existenceCovered: boolean; expectedVersion: number | null };
/** Batch 2.12: deadline types an IRS postponement can apply to. */
export const DEADLINE_POSTPONEMENT_TYPES = ['FORM_2553', 'FORM_8832', 'ALL'] as const;
export type DeadlinePostponementType = typeof DEADLINE_POSTPONEMENT_TYPES[number];
/** Batch 2.12: a documented IRS postponement recorded for one business (deadlines from periodStart until postponedTo move to postponedTo). */
export type DeadlinePostponement = { id: string; reference: string; deadlineType: DeadlinePostponementType | string; area: string; state?: string | null;
  periodStart: string; postponedTo: string; recordedBy: string; recordedAt: string; version: number };
export type DeadlinePostponementInput = { reference: string; deadlineType: DeadlinePostponementType; area: string; state: string | null;
  periodStart: string; postponedTo: string; confirmed: true };
/**
 * S corporation checklist items (batch 2.10: seven items replacing the four of batch 2.9), ticked when reviewed against supporting
 * documentation. Missing items are warnings. Ticking them never creates a valid election and is not IRS approval.
 */
export const S_CORPORATION_CHECKLIST = ['DOMESTIC_ELIGIBLE_CORPORATION', 'ELIGIBLE_SHAREHOLDERS', 'SHAREHOLDER_LIMIT', 'ONE_CLASS_OF_STOCK',
  'SHAREHOLDER_CONSENTS', 'PERMITTED_TAX_YEAR', 'FORM_2553_EVIDENCE'] as const;
export type SCorporationChecklistItem = typeof S_CORPORATION_CHECKLIST[number];
/** Batch 2.9: one dated, confirmed classification version (reports for a year use the version in effect on 31 December). */
export type TaxClassificationVersion = {
  id: string; legalEntityType: LegalEntityType; federalTaxClassification: FederalTaxClassification; effectiveFrom: string; effectiveTo?: string | null;
  evidenceReference: string; taxOwnerType?: TaxOwnerType | null; taxOwnerName?: string | null; electionType?: TaxClassificationElectionType | null;
  electionEffectiveDate?: string | null; electionFiledDate?: string | null; electionReference?: string | null; sCorporationChecklist?: string[];
  warnings?: string[]; confirmedBy: string; confirmedAt: string; origin: 'CONFIRMED' | 'MIGRATED' | string; supersededAt?: string | null; supersededBy?: string | null;
  /** Batch 2.10: 2 for the seven-item checklist; 1 (or none) for the four batch 2.9 items, which need re-review. */
  sCorporationChecklistVersion?: number | null; sCorporationChecklistNeedsReview?: boolean;
};
export type TaxElectionRule = { situation: string; legalEntityType?: LegalEntityType | null; classification: FederalTaxClassification;
  requiredElection?: TaxClassificationElectionType | null; note: string };
export type TaxClassificationHistory = { legalEntityId: string; legalEntityName: string; versions: TaxClassificationVersion[];
  electionRules: TaxElectionRule[]; sCorporationChecklist: string[];
  /** Batch 2.10: the server's item text, the confirmation label ("Reviewed against supporting documentation") and the note that it is not an election. */
  sCorporationChecklistText?: Record<string, string>; sCorporationChecklistConfirmation?: string; sCorporationChecklistNote?: string;
  /** Batch 2.12: tax profile, recorded IRS postponements, the note that timing checks never make an election valid, and the state holiday table. */
  taxProfile?: LegalEntityTaxProfile | null; postponements?: DeadlinePostponement[]; electionCheckNote?: string | null;
  stateHolidayNote?: string | null; stateHolidayStates?: string[] };
export type LegalEntityTaxClassificationInput = {
  legalEntityType: LegalEntityType; federalTaxClassification: FederalTaxClassification; effectiveFrom: string;
  documentationReference?: string | null; confirmed: boolean; expectedVersion: number | null;
  taxOwnerType?: TaxOwnerType | null; taxOwnerName?: string | null; taxOwnerReference?: string | null;
  electionType?: TaxClassificationElectionType | null; electionEffectiveDate?: string | null; electionReference?: string | null;
  /** Batch 2.9: filing date (timing warnings) and confirmed S corporation checklist items. Confirming needs documentationReference. */
  electionFiledDate?: string | null; sCorporationChecklist?: SCorporationChecklistItem[] | null;
  /** Batch 2.10: required (true) when checklist items are ticked: "Reviewed against supporting documentation". */
  sCorporationChecklistReviewed?: boolean | null;
};
/** Federal classifications each legal form may use (matches the server). */
export const ALLOWED_TAX_CLASSIFICATIONS: Record<LegalEntityType, FederalTaxClassification[]> = {
  INDIVIDUAL: ['INDIVIDUAL'],
  SOLE_PROPRIETORSHIP: ['INDIVIDUAL'],
  SINGLE_MEMBER_LLC: ['DISREGARDED_ENTITY', 'C_CORPORATION', 'S_CORPORATION'],
  MULTI_MEMBER_LLC: ['PARTNERSHIP', 'C_CORPORATION', 'S_CORPORATION'],
  PARTNERSHIP: ['PARTNERSHIP', 'C_CORPORATION', 'S_CORPORATION'],
  C_CORPORATION: ['C_CORPORATION', 'S_CORPORATION'],
  S_CORPORATION: ['S_CORPORATION'],
  TRUST_OR_ESTATE: ['ESTATE', 'NON_GRANTOR_TRUST', 'GRANTOR_TRUST'],
  OTHER: ['INDIVIDUAL', 'DISREGARDED_ENTITY', 'PARTNERSHIP', 'S_CORPORATION', 'C_CORPORATION', 'ESTATE', 'NON_GRANTOR_TRUST', 'GRANTOR_TRUST', 'OTHER'],
};
/**
 * The election a classification needs (matches the server): Form 2553 for an S corporation (except an S corporation by legal
 * form, or OTHER), Form 8832 for an LLC or partnership taxed as a C corporation. It must be effective on or before the
 * classification's effective date.
 */
export function requiredTaxElection(type: LegalEntityType, classification: FederalTaxClassification): TaxClassificationElectionType | null {
  if (classification === 'S_CORPORATION' && type !== 'S_CORPORATION' && type !== 'OTHER') return 'FORM_2553';
  if (classification === 'C_CORPORATION' && ['SINGLE_MEMBER_LLC', 'MULTI_MEMBER_LLC', 'PARTNERSHIP'].includes(type)) return 'FORM_8832';
  return null;
}
export type OpeningBalanceAllocation = {
  accountCode: string; propertyId?: string; ownerClientId?: string; tenantId?: string; amount: number;
};
export type OpeningBalanceRequest = {
  requestId: string; date: string; evidence: string; allocations: OpeningBalanceAllocation[];
};
export type OpeningBalanceJournal = { id: string; reference: string; entryDate: string; totalDebit: number };
export type OpeningBalanceProperty = { id: string; name?: string | null; address?: string | null };
export type OpeningBalanceTenant = { id: string; firstName?: string | null; lastName?: string | null; email?: string | null };
export type PropertyBankPurpose = 'TRUST_OPERATING' | 'SECURITY_DEPOSIT_TRUST' | 'STRIPE_CLEARING' | 'PM_OPERATING';
export type PropertyBankRoute = {
  id: string; organizationId: string; propertyId: string; bankAccountType: PropertyBankPurpose;
  currency: string; bankAccountId: string; updatedBy: string; updatedAt: string; version: number;
};
export type PropertyBankRouteRequest = { bankAccountId: string; expectedVersion: number | null; reason: string };
export type BankMigrationPreview = {
  bankAccountId: string; fingerprint: string; journalCount: number; lineCount: number;
  cashBalance: number; exceptions: string[];
  transactionCount?: number; unassignedTransactionCount?: number; unassignedLineCount?: number;
};
export type LegacyBankAccount = { id: string; accountCode: string; accountName: string; active: boolean; bankAccountId: string | null };
export type LegacyOwnershipTransaction = { id: string; journalId: string; date: string; type: string;
  reference: string | null; externalId: string; amount: number; currency: string; debitAccount: string; creditAccount: string };
export type PostingOwnershipItem = {
  journalId: string; entryDate: string; reference: string | null; memo: string | null; entryType: string | null; currency: string;
  amount: number; propertyId: string | null; accountCodes: string[]; unownedLineCount: number;
  suggestedLegalEntityId: string | null; suggestedLegalEntityName: string | null; suggestion: string | null;
};
export type PostingOwnershipReview = { journalCount: number; truncated: boolean; items: PostingOwnershipItem[] };
export type LegacyOwnershipPreview = { accountId: string; accountCode: string; accountName: string;
  legalEntityId: string; legalEntityName: string; currency: string; bankAccountId: string | null; fingerprint: string;
  journalCount: number; lineCount: number; unassignedTransactionCount: number; unassignedLineCount: number;
  cashBalance: number; transactions: LegacyOwnershipTransaction[]; exceptions: string[] };
export type BankReconciliationEvidence = {
  statementId: string; statementBalance: number; outstandingDeposits: number; outstandingWithdrawals: number;
  bookBalance: number; liabilityBalance: number; beneficiaryBalance: number; beneficiaries: number;
  fingerprint: string; exceptions: string[];
  beneficiaryEvidenceBasis?: string; independentValidationStatus?: string; closeBlockers?: string[];
};

export type AccountingPeriodDto = {
  id: string;
  organizationId: string;
  periodStart: string;
  periodEnd: string;
  status: AccountingPeriodStatus;
  closedAt?: string | null;
  closedByUserId?: string | null;
  reopenedAt?: string | null;
  reopenedByUserId?: string | null;
  notes?: string | null;
};

export type BankStatementLineRequest = {
  transactionDate: string;
  direction: AccountingDirection;
  amount: number;
  description?: string | null;
  reference?: string | null;
  externalId?: string | null;
};

export type BankStatementImportRequest = {
  bankAccountId: string;
  statementStart: string;
  statementEnd: string;
  openingBalance: number;
  closingBalance: number;
  sourceFilename?: string | null;
  lines?: BankStatementLineRequest[] | null;
};

export type BankStatementLineResponse = BankStatementLineRequest & {
  id: string;
  matchStatus: BankStatementLineMatchStatus;
  matchedTransactionId?: string | null;
  matchedJournalEntryId?: string | null;
  matchedAt?: string | null;
  matchedByUserId?: string | null;
};

export type BankStatementImportResponse = {
  id: string;
  organizationId: string;
  bankAccountId: string;
  statementStart: string;
  statementEnd: string;
  openingBalance: number;
  closingBalance: number;
  sourceFilename?: string | null;
  importedAt?: string | null;
  importedByUserId?: string | null;
  lineCount: number;
  lines?: BankStatementLineResponse[] | null;
};

export type TrustReconciliationRequest = {
  bankAccountId: string;
  bankAccountCode?: string | null;
  periodStart: string;
  periodEnd: string;
  bankStatementBalance: number;
  liabilityAccountCodes?: string[] | null;
  unmatchedDepositTotal?: number | null;
  unmatchedWithdrawalTotal?: number | null;
  notes?: string | null;
};

export type TrustReconciliationResponse = {
  id: string;
  organizationId: string;
  bankAccountId: string;
  bankAccountCode?: string | null;
  periodStart: string;
  periodEnd: string;
  bankStatementBalance: number;
  adjustedBankBalance: number;
  bookTrustBankBalance: number;
  trustLiabilityBalance: number;
  bankVsBookDifference: number;
  bookVsLiabilityDifference: number;
  unmatchedDepositTotal?: number | null;
  unmatchedWithdrawalTotal?: number | null;
  liabilityAccountCodes?: string[] | null;
  status: ReconciliationStatus;
  preparedAt?: string | null;
  preparedByUserId?: string | null;
  closedAt?: string | null;
  closedByUserId?: string | null;
  notes?: string | null;
};

export type LedgerReportLineDto = {
  currency?: string;
  openingBalance?: number;
  entryDate: string;
  journalEntryId: string;
  transactionId?: string | null;
  accountCode: string;
  accountName: string;
  debit: number;
  credit: number;
  runningBalance: number;
  propertyId?: string | null;
  unitId?: string | null;
  tenantId?: string | null;
  ownerClientId?: string | null;
  maintenanceRequestId?: string | null;
  memo?: string | null;
};

export type TrialBalanceLineDto = {
  currency?: string;
  openingBalance?: number;
  closingBalance?: number;
  accountCode: string;
  accountName: string;
  accountType: LedgerAccountType;
  totalDebit: number;
  totalCredit: number;
  netDebit: number;
  netCredit: number;
};

export type PostingRuleDto = {
  id: string;
  organizationId: string;
  ruleCode: string;
  displayName: string;
  transactionType?: string | null;
  direction: AccountingDirection;
  sourceRefType?: string | null;
  externalSource?: string | null;
  gatewayStatusPresent?: boolean | null;
  ownerContextMode: PostingRuleContextMode;
  debitAccountCode: string;
  debitAccountName: string;
  creditAccountCode: string;
  creditAccountName: string;
  debitMemo?: string | null;
  creditMemo?: string | null;
  priority: number;
  active: boolean;
  systemDefault: boolean;
  version: number;
};

export type PostingRuleUpsertRequest = {
  displayName: string;
  transactionType?: string | null;
  direction: AccountingDirection;
  sourceRefType?: string | null;
  externalSource?: string | null;
  gatewayStatusPresent?: boolean | null;
  ownerContextMode?: PostingRuleContextMode | null;
  debitAccountCode: string;
  debitAccountName: string;
  creditAccountCode: string;
  creditAccountName: string;
  debitMemo?: string | null;
  creditMemo?: string | null;
  priority?: number | null;
  active?: boolean | null;
};

export type JournalBackfillResponse = {
  transactionsScanned: number;
  journalsCreated: number;
  alreadyPosted: number;
  failed: number;
  errors: string[];
};

export type AccountingSetupResponse = {
  organizationId: string;
  accounts: ChartOfAccountDto[];
  bankAccounts: AccountingBankAccountDto[];
};

export type AccountingPage<T> = { content: T[]; totalElements: number; totalPages: number; number: number };
export type BankMatchCandidate = { id: string; transactionDate: string; description?: string; reference?: string; amount: number; currency: string };

export type AccountingReportBasis = 'POSTED_LEDGER' | 'CASH' | 'ACCRUAL';
export type FinancialStatement = {
  type: string; basis: string; currency: string; propertyId: string | null; legalEntityId?: string | null; from: string; to: string;
  rows: Array<{ code: string; name: string; section: string; amount: number }>;
  totals: Record<string, number>; warnings: string[];
  coverage?: { status: 'COMPLETE' | 'BLOCKED'; journalCount: number; recognizedJournalCount: number;
    unclassifiedJournalCount: number; disclosures: string[] } | null;
};
export type BankAccountCreateRequest = {
  displayName: string; accountCode: string; bankAccountType: string; currency: string; lastFour: string | null;
};

/** One owner's funds held in trust for a property. */
export type OwnerFunds = { ownerClientId: string; currency: string; held: number };

@Injectable({ providedIn: 'root' })
export class AccountingService {
  private readonly displayPreferences = inject(DisplayPreferencesService);
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiGatewayUrl}/transactions`;
  dataHealth(org: string): Observable<AccountingHealth> {
    return this.http.get<AccountingHealth>(`${this.base}/organizations/${encodeURIComponent(org)}/accounting/data-health`, { withCredentials: true });
  }
  accountantPreview(org: string, scope: AccountantScope): Observable<AccountantPreview> {
    return this.http.get<AccountantPreview>(`${this.base}/organizations/${encodeURIComponent(org)}/accounting/reports/accountant-package`, { params: { ...scope }, withCredentials: true });
  }
  accountantDownload(org: string, scope: AccountantScope): Observable<Blob> {
    return this.http.get(`${this.base}/organizations/${encodeURIComponent(org)}/accounting/reports/accountant-package`, { params: { ...scope, download: true }, responseType: 'blob', withCredentials: true });
  }

  getCapabilities(orgId: string): Observable<AccountingCapabilities> {
    return this.http.get<AccountingCapabilities>(`${this.accountingBase(orgId)}/capabilities`, { withCredentials: true });
  }

  createBankAccount(orgId: string, input: BankAccountCreateRequest): Observable<AccountingBankAccountDto> {
    return this.http.post<AccountingBankAccountDto>(`${this.accountingBase(orgId)}/bank-accounts`, input, { withCredentials: true });
  }

  legalEntities(orgId: string): Observable<AccountingLegalEntity[]> {
    return this.http.get<AccountingLegalEntity[]>(`${this.accountingBase(orgId)}/legal-entities`, { withCredentials: true });
  }

  createLegalEntity(orgId: string, input: { code: string; name: string }): Observable<AccountingLegalEntity> {
    return this.http.post<AccountingLegalEntity>(`${this.accountingBase(orgId)}/legal-entities`, input, { withCredentials: true });
  }

  updateLegalEntityTaxClassification(orgId: string, entityId: string, input: LegalEntityTaxClassificationInput): Observable<AccountingLegalEntity> {
    return this.http.put<AccountingLegalEntity>(`${this.accountingBase(orgId)}/legal-entities/${encodeURIComponent(entityId)}/tax-classification`,
      input, { withCredentials: true });
  }

  /** Batch 2.12: filing state, formation date and "existence covered" confirmation (configure permission, audited). */
  updateLegalEntityTaxProfile(orgId: string, entityId: string, input: LegalEntityTaxProfileInput): Observable<LegalEntityTaxProfile> {
    return this.http.put<LegalEntityTaxProfile>(`${this.accountingBase(orgId)}/legal-entities/${encodeURIComponent(entityId)}/tax-profile`,
      input, { withCredentials: true });
  }

  /** Batch 2.12: record a documented IRS postponement for a business (configure permission, audited). */
  addDeadlinePostponement(orgId: string, entityId: string, input: DeadlinePostponementInput): Observable<DeadlinePostponement> {
    return this.http.post<DeadlinePostponement>(`${this.accountingBase(orgId)}/legal-entities/${encodeURIComponent(entityId)}/deadline-postponements`,
      input, { withCredentials: true });
  }

  deleteDeadlinePostponement(orgId: string, entityId: string, postponementId: string, expectedVersion: number): Observable<void> {
    return this.http.delete<void>(`${this.accountingBase(orgId)}/legal-entities/${encodeURIComponent(entityId)}/deadline-postponements/${encodeURIComponent(postponementId)}`,
      { params: { expectedVersion: String(expectedVersion) }, withCredentials: true });
  }

  /** Batch 2.9: dated classification versions (newest first) with the election rules table. */
  legalEntityTaxClassificationHistory(orgId: string, entityId: string): Observable<TaxClassificationHistory> {
    return this.http.get<TaxClassificationHistory>(`${this.accountingBase(orgId)}/legal-entities/${encodeURIComponent(entityId)}/tax-classification-history`,
      { withCredentials: true });
  }

  assignBankOwnership(orgId: string, bankId: string, legalEntityId: string): Observable<void> {
    return this.http.put<void>(`${this.accountingBase(orgId)}/bank-accounts/${bankId}/ownership`, { legalEntityId }, { withCredentials: true });
  }

  previewBankMigration(orgId: string, bankId: string): Observable<BankMigrationPreview> {
    return this.http.get<BankMigrationPreview>(`${this.accountingBase(orgId)}/bank-accounts/${bankId}/migration`, { withCredentials: true });
  }

  legacyBankAccounts(orgId: string): Observable<LegacyBankAccount[]> {
    return this.http.get<LegacyBankAccount[]>(`${this.accountingBase(orgId)}/legacy-bank-accounts`, { withCredentials: true });
  }

  previewLegacyOwnership(orgId: string, accountId: string, legalEntityId: string): Observable<LegacyOwnershipPreview> {
    return this.http.get<LegacyOwnershipPreview>(`${this.accountingBase(orgId)}/legacy-bank-accounts/${accountId}/review`, {
      params: { legalEntityId }, withCredentials: true,
    });
  }

  applyLegacyOwnership(orgId: string, accountId: string, input: { legalEntityId: string; fingerprint: string; reason: string; confirmed: boolean }): Observable<LegacyOwnershipPreview> {
    return this.http.post<LegacyOwnershipPreview>(`${this.accountingBase(orgId)}/legacy-bank-accounts/${accountId}/ownership`, input, { withCredentials: true });
  }

  /** Postings no legal entity owns yet (they block cash/accrual entity reports), each with a suggested owner. */
  postingOwnershipReview(orgId: string): Observable<PostingOwnershipReview> {
    return this.http.get<PostingOwnershipReview>(`${this.accountingBase(orgId)}/ownership-review`, { withCredentials: true });
  }

  assignPostingOwnership(orgId: string, journalId: string, input: { legalEntityId: string; reason: string; confirmed: boolean }):
    Observable<{ journalId: string; legalEntityId: string; lineCount: number }> {
    return this.http.post<{ journalId: string; legalEntityId: string; lineCount: number }>(
      `${this.accountingBase(orgId)}/ownership-review/${journalId}`, input, { withCredentials: true });
  }

  postOpeningBalance(orgId: string, bankId: string, input: OpeningBalanceRequest): Observable<OpeningBalanceJournal> {
    return this.http.post<OpeningBalanceJournal>(`${this.accountingBase(orgId)}/bank-accounts/${bankId}/opening`, input, { withCredentials: true });
  }

  propertyBankRoutes(orgId: string): Observable<PropertyBankRoute[]> {
    return this.http.get<PropertyBankRoute[]>(`${this.accountingBase(orgId)}/property-bank-routes`, { withCredentials: true });
  }

  savePropertyBankRoute(orgId: string, propertyId: string, type: PropertyBankPurpose, currency: string, input: PropertyBankRouteRequest): Observable<PropertyBankRoute> {
    return this.http.put<PropertyBankRoute>(`${this.accountingBase(orgId)}/property-bank-routes/${encodeURIComponent(propertyId)}/${type}/${encodeURIComponent(currency)}`, input, { withCredentials: true });
  }

  openingProperties(orgId: string, page = 0): Observable<AccountingPage<OpeningBalanceProperty>> {
    return this.http.get<AccountingPage<OpeningBalanceProperty>>(`${environment.apiGatewayUrl}/property-service/organizations/${orgId}/properties`, {
      params: { page, size: 100, sort: 'name,asc' }, withCredentials: true,
    });
  }

  openingTenants(orgId: string, propertyId: string, page = 0): Observable<AccountingPage<OpeningBalanceTenant>> {
    return this.http.get<AccountingPage<OpeningBalanceTenant>>(`${environment.apiGatewayUrl}/property-service/organizations/${orgId}/properties/${propertyId}/tenants`, {
      params: { page, size: 100, sort: 'lastName,asc' }, withCredentials: true,
    });
  }

  reconciliationEvidence(orgId: string, bankId: string, from: string, to: string): Observable<BankReconciliationEvidence> {
    return this.http.get<BankReconciliationEvidence>(`${this.accountingBase(orgId)}/bank-accounts/${bankId}/reconciliation-evidence`, {
      params: { from, to }, withCredentials: true,
    });
  }

  applyBankMigration(orgId: string, bankId: string, input: { fingerprint: string; reason: string }): Observable<BankMigrationPreview> {
    return this.http.post<BankMigrationPreview>(`${this.accountingBase(orgId)}/bank-accounts/${bankId}/migration`, input, { withCredentials: true });
  }

  financialStatement(orgId: string, type: string, from: string, to: string, currency: string, legalEntityId?: string,
    basis: AccountingReportBasis = 'POSTED_LEDGER'): Observable<FinancialStatement> {
    let params = new HttpParams({ fromObject: { type, from, to, currency, basis } });
    if (legalEntityId) params = params.set('legalEntityId', legalEntityId);
    return this.http.get<FinancialStatement>(`${this.accountingBase(orgId)}/reports/financial-statement`, {
      params, withCredentials: true,
    });
  }

  mapCashFlow(orgId: string, accountCode: string, category: string): Observable<void> {
    return this.http.put<void>(`${this.accountingBase(orgId)}/chart-of-accounts/${encodeURIComponent(accountCode)}/cash-flow`,
      { category }, { withCredentials: true });
  }

  setupDefaults(orgId: string, currency = this.displayPreferences.currentCurrency): Observable<AccountingSetupResponse> {
    const params = new HttpParams().set('currency', currency);
    return this.http.post<AccountingSetupResponse>(
      `${this.accountingBase(orgId)}/setup/defaults`,
      null,
      { params, withCredentials: true },
    );
  }

  listChartOfAccounts(orgId: string): Observable<ChartOfAccountDto[]> {
    return this.http.get<ChartOfAccountDto[]>(`${this.accountingBase(orgId)}/chart-of-accounts`, {
      withCredentials: true,
    });
  }

  createChartAccount(orgId: string, input: ChartAccountInput): Observable<ChartOfAccountDto> {
    return this.http.post<ChartOfAccountDto>(`${this.accountingBase(orgId)}/chart-of-accounts`, input, { withCredentials: true });
  }

  getChartAccountForEdit(orgId: string, accountCode: string): Observable<ChartAccountEditView> {
    return this.http.get<ChartAccountEditView>(
      `${this.accountingBase(orgId)}/chart-of-accounts/${encodeURIComponent(accountCode)}/edit`, { withCredentials: true });
  }

  updateChartAccount(orgId: string, accountCode: string, input: ChartAccountUpdateInput): Observable<ChartAccountEditView> {
    return this.http.put<ChartAccountEditView>(
      `${this.accountingBase(orgId)}/chart-of-accounts/${encodeURIComponent(accountCode)}`, input, { withCredentials: true });
  }

  listBankAccounts(orgId: string): Observable<AccountingBankAccountDto[]> {
    return this.http.get<AccountingBankAccountDto[]>(`${this.accountingBase(orgId)}/bank-accounts`, {
      withCredentials: true,
    });
  }

  listPostingRules(orgId: string): Observable<PostingRuleDto[]> {
    return this.http.get<PostingRuleDto[]>(`${this.accountingBase(orgId)}/posting-rules`, {
      withCredentials: true,
    });
  }

  resetPostingRules(orgId: string, actorUserId?: string | null): Observable<PostingRuleDto[]> {
    return this.http.post<PostingRuleDto[]>(
      `${this.accountingBase(orgId)}/posting-rules/reset-defaults`,
      null,
      { params: this.optionalActorParams(actorUserId), withCredentials: true },
    );
  }

  upsertPostingRule(
    orgId: string,
    ruleCode: string,
    payload: PostingRuleUpsertRequest,
    actorUserId?: string | null,
  ): Observable<PostingRuleDto> {
    return this.http.post<PostingRuleDto>(
      `${this.accountingBase(orgId)}/posting-rules/${encodeURIComponent(ruleCode)}`,
      payload,
      { params: this.optionalActorParams(actorUserId), withCredentials: true },
    );
  }

  listPeriods(orgId: string): Observable<AccountingPeriodDto[]> {
    return this.http.get<AccountingPeriodDto[]>(`${this.accountingBase(orgId)}/periods`, {
      withCredentials: true,
    });
  }

  closePeriod(orgId: string, year: number, month: number, actorUserId: string, notes?: string | null): Observable<AccountingPeriodDto> {
    return this.http.post<AccountingPeriodDto>(
      `${this.accountingBase(orgId)}/periods/${year}/${month}/close`,
      null,
      { params: this.actorParams(actorUserId, notes), withCredentials: true },
    );
  }

  reopenPeriod(orgId: string, year: number, month: number, actorUserId: string, notes?: string | null): Observable<AccountingPeriodDto> {
    return this.http.post<AccountingPeriodDto>(
      `${this.accountingBase(orgId)}/periods/${year}/${month}/reopen`,
      null,
      { params: this.actorParams(actorUserId, notes), withCredentials: true },
    );
  }

  importBankStatement(
    orgId: string,
    payload: BankStatementImportRequest,
    actorUserId: string,
  ): Observable<BankStatementImportResponse> {
    return this.http.post<BankStatementImportResponse>(
      `${this.accountingBase(orgId)}/bank-statements`,
      payload,
      { params: this.actorParams(actorUserId), withCredentials: true },
    );
  }

  listBankStatementLines(orgId: string, statementImportId: string): Observable<BankStatementLineResponse[]> {
    return this.http.get<BankStatementLineResponse[]>(
      `${this.accountingBase(orgId)}/bank-statements/${statementImportId}/lines`,
      { withCredentials: true },
    );
  }

  listBankStatements(orgId: string, bankAccountId: string, page = 0): Observable<AccountingPage<BankStatementImportResponse>> {
    return this.http.get<AccountingPage<BankStatementImportResponse>>(`${this.accountingBase(orgId)}/bank-statements`, {
      params: new HttpParams().set('bankAccountId', bankAccountId).set('page', page), withCredentials: true,
    });
  }

  listMatchCandidates(orgId: string, statementId: string, lineId: string, page = 0): Observable<AccountingPage<BankMatchCandidate>> {
    return this.http.get<AccountingPage<BankMatchCandidate>>(`${this.accountingBase(orgId)}/bank-statements/${statementId}/lines/${lineId}/candidates`, {
      params: new HttpParams().set('page', page), withCredentials: true,
    });
  }

  listJournalMatchCandidates(orgId: string, statementId: string, lineId: string, page = 0): Observable<AccountingPage<BankMatchCandidate>> {
    return this.http.get<AccountingPage<BankMatchCandidate>>(`${this.accountingBase(orgId)}/bank-statements/${statementId}/lines/${lineId}/journal-candidates`, {
      params: new HttpParams().set('page', page), withCredentials: true,
    });
  }

  matchBankStatementJournal(orgId: string, statementId: string, lineId: string, journalId: string): Observable<BankStatementLineResponse> {
    return this.http.post<BankStatementLineResponse>(`${this.accountingBase(orgId)}/bank-statements/${statementId}/lines/${lineId}/match-journal/${journalId}`, null, { withCredentials: true });
  }

  matchBankStatementLine(
    orgId: string,
    statementImportId: string,
    lineId: string,
    transactionId: string,
    actorUserId: string,
  ): Observable<BankStatementLineResponse> {
    return this.http.post<BankStatementLineResponse>(
      `${this.accountingBase(orgId)}/bank-statements/${statementImportId}/lines/${lineId}/match/${transactionId}`,
      null,
      { params: this.actorParams(actorUserId), withCredentials: true },
    );
  }

  prepareTrustReconciliation(
    orgId: string,
    payload: TrustReconciliationRequest,
    actorUserId: string,
  ): Observable<TrustReconciliationResponse> {
    return this.http.post<TrustReconciliationResponse>(
      `${this.accountingBase(orgId)}/trust-reconciliations`,
      payload,
      { params: this.actorParams(actorUserId), withCredentials: true },
    );
  }

  listTrustReconciliations(orgId: string, bankAccountId: string): Observable<TrustReconciliationResponse[]> {
    const params = new HttpParams().set('bankAccountId', bankAccountId);
    return this.http.get<TrustReconciliationResponse[]>(`${this.accountingBase(orgId)}/trust-reconciliations`, {
      params,
      withCredentials: true,
    });
  }

  closeTrustReconciliation(
    orgId: string,
    reconciliationId: string,
    actorUserId: string,
    reviewNote?: string | null,
  ): Observable<TrustReconciliationResponse> {
    let params = this.actorParams(actorUserId);
    if (reviewNote?.trim()) params = params.set('reviewNote', reviewNote.trim());
    return this.http.post<TrustReconciliationResponse>(
      `${this.accountingBase(orgId)}/trust-reconciliations/${reconciliationId}/close`,
      null,
      { params, withCredentials: true },
    );
  }

  /** Undo a match, or bring back an ignored line. */
  unmatchBankStatementLine(orgId: string, statementId: string, lineId: string, reason: string): Observable<BankStatementLineResponse> {
    return this.http.post<BankStatementLineResponse>(
      `${this.accountingBase(orgId)}/bank-statements/${statementId}/lines/${lineId}/unmatch`,
      null,
      { params: new HttpParams().set('reason', reason), withCredentials: true },
    );
  }

  ignoreBankStatementLine(orgId: string, statementId: string, lineId: string, reason: string): Observable<BankStatementLineResponse> {
    return this.http.post<BankStatementLineResponse>(
      `${this.accountingBase(orgId)}/bank-statements/${statementId}/lines/${lineId}/ignore`,
      null,
      { params: new HttpParams().set('reason', reason), withCredentials: true },
    );
  }

  getGeneralLedger(
    orgId: string,
    from: string,
    to: string,
    accountCode?: string | null,
    currency = this.displayPreferences.currentCurrency,
  ): Observable<LedgerReportLineDto[]> {
    let params = new HttpParams().set('from', from).set('to', to).set('currency', currency);
    if (accountCode?.trim()) params = params.set('accountCode', accountCode.trim());
    return this.http.get<LedgerReportLineDto[]>(`${this.accountingBase(orgId)}/reports/general-ledger`, {
      params,
      withCredentials: true,
    });
  }

  getTrialBalance(orgId: string, from: string, to: string, currency = this.displayPreferences.currentCurrency): Observable<TrialBalanceLineDto[]> {
    const params = new HttpParams().set('from', from).set('to', to).set('currency', currency);
    return this.http.get<TrialBalanceLineDto[]>(`${this.accountingBase(orgId)}/reports/trial-balance`, {
      params,
      withCredentials: true,
    });
  }

  backfillJournals(orgId: string, actorUserId?: string | null): Observable<JournalBackfillResponse> {
    return this.http.post<JournalBackfillResponse>(
      `${this.accountingBase(orgId)}/journals/backfill`,
      null,
      { params: this.optionalActorParams(actorUserId), withCredentials: true },
    );
  }

  /** What each owner of a property holds in trust today (posting refuses to overdraw it). */
  ownerFunds(orgId: string, propertyId: string): Observable<OwnerFunds[]> {
    return this.http.get<OwnerFunds[]>(`${this.accountingBase(orgId)}/owner-funds`, { params: { propertyId }, withCredentials: true });
  }

  private accountingBase(orgId: string): string {
    return `${this.base}/organizations/${orgId}/accounting`;
  }

  private actorParams(actorUserId: string, notes?: string | null): HttpParams {
    let params = new HttpParams();
    if (notes?.trim()) params = params.set('notes', notes.trim());
    return params;
  }

  private optionalActorParams(actorUserId?: string | null): HttpParams {
    return new HttpParams();
  }
}
